import { Tooltip } from "@/components/toolTip";
import Icon from "@imminently/decisively-ui/Icon";

import type { ReleaseDocument } from "@packages/commons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useNotify } from "@/common/notifications";
import { setSelectedDocument } from "@/pages/documents/selectedDocument.slice";
import { findReleaseDocument } from "@packages/commons";

import { useCurrentRelease } from "../common/hooks";
import { resolveReleaseDocument, trimDocName } from "./documentNames";
import { useRouting } from "./use-routing";

export {
  DISALLOWED_CHARACTERS_MESSAGE,
  DISALLOWED_CHARACTERS_REGEX,
  getReleaseDocumentDisplayName,
  resolveReleaseDocument,
  sanitizeDocName,
  trimDocName,
  validateDocName,
} from "./documentNames";

export const isFile = (node: ReleaseDocument): boolean =>
  Object.prototype.hasOwnProperty.call(node, "name") ||
  Object.prototype.hasOwnProperty.call(node, "reference") ||
  Object.prototype.hasOwnProperty.call(node, "type");

/** Utility to determine if a file is a phantom (reference exists but name has been is removed) */
export const isPhantomFile = (node: ReleaseDocument): boolean =>
  Object.prototype.hasOwnProperty.call(node, "reference") && !Object.prototype.hasOwnProperty.call(node, "name");

export const getFileIcon = (doc: ReleaseDocument) => {
  if (isPhantomFile(doc))
    return (
      <Tooltip title="Corrupt document - use 'View' to see the contents or 'Delete' the document">
        <Icon name="warning" />
      </Tooltip>
    );
  if (!isFile(doc)) return <Icon name="folder-open" />;
  if (doc.type === "rulesheet") return <Icon name="datasheet" />;
  if (doc.type === "datatable") return <Icon name="table" />;
  if (doc.type === "mapping") return <Icon name="map" />;
  if (doc.type === "connection") return <Icon name="linking" />;
  if (doc.version > 1) return <Icon name="documents" />;
  return <Icon name="document" />;
};

/** A hook to open a document in a new window */
export const viewDocument = (id: string) => {
  window.open(`/doc/${id}`, "_blank", "scrollbars=yes,resizable=yes,top=500,left=500,width=1280,height=720");
};

/** A hook to allow navigating to a document based on path only */
export const useNavigateToDocument = () => {
  const release = useCurrentRelease();
  const { goToPage } = useRouting();
  const { error } = useNotify();

  return (path: string, newWind = false) => {
    // first, find the document. Make sure to normalise both paths
    const document = resolveReleaseDocument(release?.documents, path);
    if (document) {
      const documentPath = trimDocName(document.name);
      // then navigate to it
      if (newWind) {
        viewDocument(document.reference);
      } else {
        goToPage("/build", { documentPath, documentId: document.reference });
      }
    } else {
      error(`Could not find document for ${path}. It has likely been renamed or deleted.`);
    }
  };
};

export const useDocumentRouting = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const notify = useNotify();
  const { routingState, clearState } = useRouting();
  const release = useFullRelease();

  useEffect(() => {
    const { documentId, documentPath } = routingState as any;
    if (documentId && documentPath) {
      console.log("Auto-open document", routingState);
      const doc = findReleaseDocument(release?.documents, (doc) => doc.reference === documentId);
      if (!doc) {
        // if it fails, clear routing state so we don't keep trying
        clearState();
        notify.error(t("errors.cannot_view_document"));
        return;
      }
      dispatch(setSelectedDocument(doc));
    }
  }, [routingState]);
};
